import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, MapPin, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { authFetch, API } from '../utils/api';


export default function SpotSearchSelect({ value, onChange, placeholder, testId }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchSpots = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await authFetch(`${API}/api/spots/spot-names?q=${encodeURIComponent(q)}`);
      if (res.ok) {
        const data = await res.json();
        setResults(data.spots || []);
      }
    } catch {} finally { setLoading(false); }
  }, []);

  const handleInput = (e) => {
    const val = e.target.value;
    setQuery(val);
    setOpen(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchSpots(val), 250);
  };

  const selectSpot = (name) => {
    onChange(name);
    setQuery('');
    setOpen(false);
    setResults([]);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/40" />
        <input
          data-testid={testId}
          type="text"
          value={open ? query : value || ''}
          onFocus={() => { setQuery(value || ''); setOpen(true); if (value) searchSpots(value); }}
          onChange={handleInput}
          placeholder={placeholder}
          className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-xs outline-none focus:border-primary/50 transition-colors placeholder:text-muted-foreground/30"
        />
        {value && !open && (
          <button
            onClick={() => onChange('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-white/10 rounded-full"
          >
            <X className="w-3 h-3 text-muted-foreground/50" />
          </button>
        )}
      </div>
      <AnimatePresence>
        {open && results.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="absolute z-20 top-full mt-1 w-full bg-bg-paper border border-white/10 rounded-xl shadow-xl max-h-48 overflow-y-auto"
          >
            {results.map((s, i) => (
              <button
                key={`${s.name}-${i}`}
                data-testid={`spot-result-${i}`}
                onClick={() => selectSpot(s.name)}
                className="w-full text-left px-3 py-2 hover:bg-white/5 transition-colors flex items-center gap-2 border-b border-white/[0.03] last:border-0"
              >
                <MapPin className="w-3 h-3 text-muted-foreground/40 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs truncate">{s.name}</p>
                  {s.region && <p className="text-[9px] text-muted-foreground/40">{s.region}</p>}
                </div>
              </button>
            ))}
            {loading && (
              <div className="px-3 py-2 text-[10px] text-muted-foreground/40 text-center">searching...</div>
            )}
          </motion.div>
        )}
        {open && query.length >= 2 && results.length === 0 && !loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute z-20 top-full mt-1 w-full bg-bg-paper border border-white/10 rounded-xl shadow-xl px-3 py-3 text-center"
          >
            <p className="text-[10px] text-muted-foreground/40">no spots found for "{query}"</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
